import { INTERNAL_ACTION_IDS, SPECIAL_ACTIONS } from './specialActions';

export interface ActionInputField {
  name: string;
  type: string;
  isCollection?: boolean;
  required: boolean;
  allowedSources: string[];
}

export interface ActionSettingField {
  name: string;
  type: string;
  isCollection?: boolean;
  required: boolean;
  enumValues?: string[];
  defaultValue?: unknown;
}

export interface ActionOutputField {
  name: string;
  type: string;
  isCollection?: boolean;
}

export interface ActionContract {
  actionId: string;
  displayName: string;
  category?: string;
  inputs: ActionInputField[];
  settings: ActionSettingField[];
  outputs: ActionOutputField[];
}

// Raw API model based on Swagger `Action` schema.
export interface ActionPropertyTypeDescriptor {
  type: string;
  isCollection: boolean;
}

export interface ApiAction {
  actionId: string;
  actionName?: string | null;
  input?: Record<string, ActionPropertyTypeDescriptor> | null;
  output?: Record<string, ActionPropertyTypeDescriptor> | null;
  settings?: Record<string, ActionPropertyTypeDescriptor> | null;
}

const mapPropertiesToInputFields = (
  properties: Record<string, ActionPropertyTypeDescriptor> | null | undefined,
): ActionInputField[] => {
  if (!properties) {
    return [];
  }

  return Object.entries(properties).map(([name, descriptor]) => ({
    name,
    type: descriptor.type,
    isCollection: descriptor.isCollection,
    required: false,
    allowedSources: ['StepOutput', 'ProcessingJobInput', 'Constant'],
  }));
};

const mapPropertiesToSettingFields = (
  properties: Record<string, ActionPropertyTypeDescriptor> | null | undefined,
): ActionSettingField[] => {
  if (!properties) {
    return [];
  }

  return Object.entries(properties).map(([name, descriptor]) => ({
    name,
    type: descriptor.type === 'integer' ? 'number' : descriptor.type,
    isCollection: descriptor.isCollection,
    required: false,
    defaultValue: descriptor.type === 'boolean' ? false : undefined,
  }));
};

const mapPropertiesToOutputFields = (
  properties: Record<string, ActionPropertyTypeDescriptor> | null | undefined,
): ActionOutputField[] => {
  if (!properties) {
    return [];
  }

  return Object.entries(properties).map(([name, descriptor]) => ({
    name,
    type: descriptor.type,
    isCollection: descriptor.isCollection,
  }));
};

// ---- Category mapping ----

const ACTION_CATEGORIES: Record<string, string> = {
  [SPECIAL_ACTIONS.route]: 'Flow Control',
  checkCondition: 'Flow Control',
  combineConditions: 'Flow Control',
  [SPECIAL_ACTIONS.forEachPdfFile]: 'Flow Control',
  runWorkflow: 'Flow Control',

  convertToPdf: 'PDF Document',
  splitPdf: 'PDF Document',
  mergePdf: 'PDF Document',
  watermarkPdf: 'PDF Document',
  applyText: 'PDF Document',

  renderPdfPage: 'PDF Page',
  extractPdfPageText: 'PDF Page',
  getPageText: 'PDF Page',
  combineTextLayers: 'PDF Page',
  createTextLayerSummary: 'PDF Page',

  ocrImage: 'Image & Detection',
  detectFaces: 'Image & Detection',
  detectBsn: 'Image & Detection',
  detectQr: 'Image & Detection',
  blurCoordinates: 'Image & Detection',
  cutOutIds: 'Image & Detection',
  predictIdExist: 'Image & Detection',

  classify: 'Classification & Extraction',
  extractData: 'Classification & Extraction',
  extractDataV2: 'Classification & Extraction',
  extractDataPerPage: 'Classification & Extraction',
  extractPii: 'Classification & Extraction',
  extractDocumentGroupOutput: 'Classification & Extraction',
  crossMatchData: 'Classification & Extraction',

  executeLlmPrompt: 'AI',
  summarizeText: 'AI',
  createEventTimeline: 'AI',
  fillForm: 'AI',
  answerQuestions: 'AI',

  makeHttpRequest: 'Integration',
  parseEmail: 'Integration',
  speechToText: 'Integration',
};

export const CATEGORY_ORDER = [
  'Flow Control',
  'PDF Document',
  'PDF Page',
  'Image & Detection',
  'Classification & Extraction',
  'AI',
  'Integration',
  'Other',
];

const getCategoryForAction = (actionId: string): string => {
  if (INTERNAL_ACTION_IDS.has(actionId)) return '_internal';
  return ACTION_CATEGORIES[actionId] ?? 'Other';
};

/**
 * Group actions by category, filtering out internal actions.
 * Returns an ordered Map following CATEGORY_ORDER.
 */
export const groupActionsByCategory = (
  actions: ActionContract[],
): Map<string, ActionContract[]> => {
  const groups = new Map<string, ActionContract[]>();

  actions.forEach((action) => {
    const cat = action.category ?? 'Other';
    if (cat === '_internal') return;
    // Hide obsolete actions from add menus — they can still appear if already in a workflow
    if (action.displayName.toUpperCase().includes('OBSOLETE')) return;
    const list = groups.get(cat) ?? [];
    list.push(action);
    groups.set(cat, list);
  });

  // Return in defined order
  const ordered = new Map<string, ActionContract[]>();
  CATEGORY_ORDER.forEach((cat) => {
    const list = groups.get(cat);
    if (list && list.length > 0) ordered.set(cat, list);
  });
  // Add any unexpected categories at the end
  groups.forEach((list, cat) => {
    if (!ordered.has(cat)) ordered.set(cat, list);
  });

  return ordered;
};

export const mapApiActionToContract = (action: ApiAction): ActionContract => {
  const displayName = action.actionName ?? action.actionId;

  return {
    actionId: action.actionId,
    displayName,
    category: getCategoryForAction(action.actionId),
    inputs: mapPropertiesToInputFields(action.input),
    settings: mapPropertiesToSettingFields(action.settings),
    outputs: mapPropertiesToOutputFields(action.output),
  };
};

export const mapApiActionsToContracts = (actions: ApiAction[]): ActionContract[] =>
  actions.map(mapApiActionToContract);

// ---- Type display ----

// Pretty-format a raw API type string for display next to a field label.
// Examples:
//   fileId       → FileId
//   boolean      → bool
//   integer      → int
//   text         → string
//   aiSettings   → AiSettings
//   any          → any
//   detectedObject → DetectedObject
export const formatTypeLabel = (
  type: string | undefined | null,
  isCollection: boolean | undefined = false,
): string => {
  if (!type) return '';
  const t = type.toLowerCase();
  let base: string;
  switch (t) {
    case 'boolean':
      base = 'bool';
      break;
    case 'integer':
      base = 'int';
      break;
    case 'floatingnumber':
      base = 'float';
      break;
    case 'text':
      base = 'string';
      break;
    case 'number':
      base = 'number';
      break;
    case 'string':
      base = 'string';
      break;
    case 'any':
      base = 'any';
      break;
    default: {
      // PascalCase the first letter so "fileId" becomes "FileId".
      base = type.charAt(0).toUpperCase() + type.slice(1);
      break;
    }
  }

  return isCollection ? `${base}[]` : base;
};
